/**
 * Typed API client.
 *
 * Types mirror the FastAPI response models. The plan calls for generating
 * these from the OpenAPI schema so drift is a compile error (P2-12); this
 * hand-written module is the same shape and is the generation target —
 * `npx openapi-typescript http://localhost:8000/openapi.json` replaces the
 * interfaces below without touching any component.
 */

const BASE = "/api";

/**
 * Tool-declared display text: a plain string, or a bilingual dict
 * ({"fr": "...", "en": "..."}) — a plugged-in module speaks both UI
 * languages. Resolve through `src/lib/tooltext.ts`.
 */
import i18n from "../i18n";
import { getCurrentCustomerId } from "./customer";
import { sessionId } from "./session";

export type LocalizedText = string | Record<string, string>;

export type RunParam = {
  name: string;
  default: string | null;
  required: boolean;
  label: LocalizedText;
  help: LocalizedText;
  /** Known controls: "month" | "year" | "text" | "choice" | "tabs" |
   *  "password". A plugged-in module may declare anything — unknown values
   *  degrade to a text input with a visible warning in ParamsForm. */
  control: string;
  choices: string[];
  /** Operator wording per choice (plain or bilingual); absent = the value. */
  choice_labels?: Record<string, LocalizedText>;
  audience: "operator" | "advanced" | "hidden";
  sticky: boolean;
  visible_when: Record<string, string> | null;
  /** Passed to the run, never stored with it (credentials). */
  secret?: boolean;
};

/** Declared input file of a tool (empty list = one conventional Excel input). */
export type InputSlot = {
  name: string;
  label: LocalizedText;
  format: string;
  required: boolean;
  /** {param: value} — the file is needed only while every named run
   *  parameter holds that value (its declared default when unset). A tool
   *  with two sources (dropped workbook / direct pull) declares it. */
  required_when?: Record<string, string> | null;
};

export type ToolEntry = {
  key: string;
  name: string;
  phase: number;
  ordinal: number;
  display_name: LocalizedText;
  description: LocalizedText;
  version: string;
  hitl: "NONE" | "AFTER" | "BEFORE";
  required_headers: string[];
  sheet_name: string | null;
  ingest_profile: string;
  overrides: { name: string; description: string }[];
  params: RunParam[];
  /** May be absent on older backends; empty = one conventional Excel input. */
  input_slots?: InputSlot[];
};

export type PhaseGroup = { phase: number; label: string; tools: ToolEntry[] };

export type UploadResult = {
  artifact_id: string;
  display_name: string;
  byte_size: number;
  content_sha256: string;
};

export type StepState = {
  step_id?: string;
  step_key: string;
  tool_name: string;
  state: string;
  attempt?: number;
  rows_in?: number;
  rows_out: number;
  unmatched_rows: number;
  started_at?: string | null;
  finished_at?: string | null;
  rule_version: string;
  error_code: string | null;
  error_detail?: string | null;
  phase?: number;
};

/** The list-row shape: /api/jobs, /api/stats recent_jobs, /api/search. */
export type JobSummary = {
  job_id: string;
  dag_name: string;
  state: string;
  dashboard_status: string;
  created_at: string | null;
  /** The run whose produced file this run started from — a FORK. */
  forked_from_job_id?: string | null;
};

export type JobState = JobSummary & {
  created_by?: string | null;
  finished_at?: string | null;
  steps: StepState[];
  artifacts: {
    artifact_id: string;
    kind: string;
    display_name: string;
    row_count: number;
    produced_by_step: string | null;
  }[];
  /** Runs launched from this run's produced files. */
  forks?: JobSummary[];
};

export type LineageEdge = {
  artifact_id: string;
  parent_artifact_id: string;
  relation: string;
  artifact_name?: string | null;
  artifact_job_id?: string | null;
  parent_name?: string | null;
  parent_job_id?: string | null;
};

export type Lineage = {
  artifact_id: string;
  display_name: string;
  job_id?: string | null;
  chain: LineageEdge[];
  /** Files derived from this one, with the run that made each. */
  used_by?: { artifact_id: string; display_name: string; relation: string; job_id: string | null }[];
};

export type PipelineState = {
  job_id: string;
  dag: string;
  state: string;
  dashboard_status: string;
  awaiting_approval: { step_id: string; step_key: string; tool_name: string }[];
  steps: StepState[];
};

export type PendingApproval = {
  job_id: string;
  dag: string;
  step_id: string;
  step_key: string;
  tool_name: string;
  rows_out: number;
  unmatched_rows: number;
  dashboard_status: string;
  artifact_id: string | null;
  display_name: string | null;
};

export type ValidationIssue = {
  code: string;
  message: string;
  column: string | null;
  row: number | null;
  raw_value: string | null;
};

export type ValidationReport = {
  is_valid: boolean;
  sheet_name: string;
  total_rows: number;
  headers: string[];
  total_errors: number;
  errors: string[];
  issues: ValidationIssue[];
  column_stats: { column: string; empty_cells: number; invalid_format: number }[];
  truncated: number;
};

export type ApiFailure = {
  status: string;
  errors?: string[];
  detail?: string;
  diff?: DriftRow[];
  report?: ValidationReport;
};

export type DriftRow = {
  column: string;
  status: "ok" | "moved" | "renamed" | "missing" | "extra";
  expected_index: number | null;
  found_index: number | null;
  found: string | null;
};

/** A corrective action PROPOSED by the backend from run state (never by the
 *  model). The UI renders it behind explicit confirmation; nothing executes
 *  until the operator confirms. */
export type ProposedAction = {
  kind: "navigate" | "rerun" | "retry_job";
  label_key: string;
  rationale?: string;
  to?: string;                    // navigate
  tool?: string;                  // rerun
  artifact_id?: string;           // rerun
  job_id?: string;                // retry_job
};

export type AuditAnswer = {
  answer: string;
  intent: string;
  sql: string;
  sql_verdict: string;
  rows: Record<string, unknown>[];
  citations: {
    document_id: string;
    title: string;
    version: string;
    effective_from: string | null;
    section: number;
    reference: string;
  }[];
  retrieval_backend: string;
  proposed_actions?: ProposedAction[];
  /** Sources the answer was composed from: run | attachment | documents | query. */
  context_used?: string[];
  /** The reading pass over the attached workbook — facts with sheet/cell. */
  attachment_notes?: string;
};

/** One [ok]/[FAIL] line of a run's Controls sheet, structured. When the run
 *  consumed a revised file, `previous_ok`/`previous_detail` carry the same
 *  control's verdict from the run the revision was answering. */
export type RunControl = {
  ok: boolean;
  name: string;
  detail: string;
  previous_ok?: boolean | null;
  previous_detail?: string | null;
};

export type RunControlEntityLine = {
  kind: "move" | "dropped" | "unknown" | "warning";
  text: string;
};

export type RunControlsReport = {
  available: boolean;
  job_id: string;
  artifact_id?: string;
  /** The input the run consumed — the file a fix revises. */
  input_artifact_id?: string | null;
  input_display_name?: string | null;
  /** Tool key of the step that produced the workbook — what a re-run launches. */
  tool?: string | null;
  title?: string;
  run_line?: string;
  meta?: string[];
  controls?: RunControl[];
  entities?: { header: string; lines: RunControlEntityLine[] }[];
  extras?: string[];
  ok?: boolean;
  failed_count?: number;
  /** Present when this run consumed a revised file: the run it answers. */
  previous?: { job_id: string; failed_count: number } | null;
};

/** One deterministic workbook edit proposed by the agent, applied by openpyxl. */
export type EditOperation = {
  action: "set_cell" | "delete_rows" | "replace_value";
  sheet?: string | null;
  cell?: string | null;
  value?: string | number | null;
  rows?: number[] | null;
  find?: string | null;
  replace?: string | number | null;
  column?: string | null;
  reason?: string;
};

export type RevisePlan = {
  mode: "plan";
  summary: string;
  /** The agent's own caveat: the edits will NOT clear the failed controls. */
  warning?: string | null;
  operations: EditOperation[];
};

export type ReviseApplied = {
  mode: "applied";
  artifact_id: string;
  display_name: string;
  results: { action: string; changed: number }[];
  changed_total: number;
};

export type DagStep = {
  key: string;
  tool: string;
  depends_on: string[];
  /** what the step does, per language */
  description: LocalizedText;
  hitl: string;
};

export type DagInfo = {
  name: string;
  version: string;
  steps: DagStep[];
  /** Display name, per language; may be absent on older backends — fall back to `name`. */
  label?: LocalizedText | null;
  /** What the pipeline does, per language — the card's text. */
  description?: LocalizedText | null;
  /** NAME of the tool whose declared run params form the wizard's identity
   *  step; null (or absent) = the pipeline needs no identity params. */
  identity_tool?: string | null;
  /** NAME of the tool that receives the seed upload (the first step's).
   *  Absent on older backends — fall back to identity_tool. */
  entry_tool?: string | null;
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: ApiFailure,
  ) {
    super(message);
  }
}

/** In-memory access token (never localStorage — an XSS reader must not find
 *  it there). The refresh token lives in an HttpOnly cookie the JS can't read.
 *  Set by the auth context after login / silent refresh. */
let accessToken: string | null = null;
export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}

function withAuth(init?: RequestInit): RequestInit {
  // `credentials: "include"` so the HttpOnly refresh cookie travels even when
  // the dashboard is served from a different origin than the API. The default
  // ("same-origin") silently omits it there, and the session then dies at the
  // 15-minute access-token expiry with no error to explain it.
  const base: RequestInit = { ...init, credentials: "include" };
  const headers = new Headers(init?.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  // every call works on the customer chosen in the top bar
  const customerId = getCurrentCustomerId();
  if (customerId) headers.set("X-Customer-Id", customerId);
  // one browser tab: with the person, the holder of the customer's edit lease
  headers.set("X-Session-Id", sessionId());
  return { ...base, headers };
}

/** Hook installed by the auth context: exchanges the refresh cookie for a new
 *  access token. Null while signed out or under AUTH_MODE=none. */
let renewAccessToken: (() => Promise<string | null>) | null = null;
export function setTokenRenewer(fn: (() => Promise<string | null>) | null): void {
  renewAccessToken = fn;
}

/** One in-flight renewal shared by every caller: a page with six concurrent
 *  requests must not fire six refreshes and rotate the cookie six times. */
let renewalInFlight: Promise<string | null> | null = null;

async function renewOnce(): Promise<string | null> {
  if (!renewAccessToken) return null;
  renewalInFlight ??= renewAccessToken().finally(() => {
    renewalInFlight = null;
  });
  return renewalInFlight;
}

async function failureFrom(response: Response): Promise<ApiError> {
  let body: ApiFailure = { status: `HTTP_${response.status}` };
  try {
    body = (await response.json()) as ApiFailure;
  } catch {
    /* non-JSON error body */
  }
  const summary =
    body.errors?.[0] ??
    body.detail ??
    i18n.t("api.requestFailed", { status: response.status });
  return new ApiError(summary, response.status, body);
}

/**
 * Shared fetch wrapper for an absolute URL: ok-check + typed failure body.
 *
 * On a 401 it renews the access token ONCE and replays the request. The
 * scheduled 14-minute refresh is not enough on its own: browsers throttle
 * timers in background tabs, so a dashboard left open in another tab comes
 * back with an expired token and, before this, showed the user an error
 * instead of quietly renewing.
 */
async function requestUrl<T>(
  url: string,
  init?: RequestInit,
  { allowRetry = true }: { allowRetry?: boolean } = {},
): Promise<T> {
  let response = await fetch(url, withAuth(init));
  if (response.status === 401 && allowRetry && renewAccessToken) {
    const renewed = await renewOnce();
    if (renewed) {
      response = await fetch(url, withAuth(init));
    }
  }
  if (!response.ok) {
    throw await failureFrom(response);
  }
  return (await response.json()) as T;
}

function request<T>(path: string, init?: RequestInit): Promise<T> {
  return requestUrl<T>(`${BASE}${path}`, init);
}

/** The refresh call itself must never trigger the retry loop it powers. */
function requestNoRetry<T>(path: string, init?: RequestInit): Promise<T> {
  return requestUrl<T>(`${BASE}${path}`, init, { allowRetry: false });
}

/**
 * `GET /api/meta` — server-declared limits, poll intervals and the job-state
 * map. The endpoint may not exist yet on older backends (404): callers fall
 * back to the built-ins in `src/lib/states.ts` / `src/lib/config.ts`.
 */
/**
 * What the browser needs to start a sign-in against the external IdP.
 *
 * Served by the backend rather than baked into the bundle: one image must
 * run against the dev, beta and prod tenants — promotion is a configuration
 * change, never a rebuild. `configured` is false when the operator has not
 * yet filled the Entra values, so the UI can say precisely what is missing
 * instead of failing inside MSAL.
 */
export type OidcConfig = {
  authority: string;
  client_id: string;
  scopes: string[];
  redirect_uri: string;
  configured: boolean;
};

export type MetaResponse = {
  max_upload_bytes: number;
  /** uploads must be named <anything>_<customer>.xlsx (default true) */
  upload_require_customer_suffix?: boolean;
  poll_interval_ms: number;
  stats_poll_interval_ms: number;
  build: string;
  /** May be absent on older backends — treat missing as "none". */
  auth_mode?: "none" | "local" | "oidc";
  /** Present only under AUTH_MODE=oidc. All values are public by design. */
  oidc?: OidcConfig | null;
  /** What the server allows the SPA to offer. Missing = nothing extra. */
  features?: { ai: boolean };
  job_states: { name: string; category: string; terminal: boolean }[];
};

export type UsageStats = {
  totals: {
    jobs: number;
    uploads: number;
    outputs: number;
    by_state: Record<string, number>;
  };
  runs_daily: { date: string; count: number }[];
  uploads_daily: { date: string; count: number }[];
  /** `display_name` is what the dashboard shows; `tool` is the code name. */
  tool_usage: { tool: string; display_name?: LocalizedText; count: number }[];
  recent_jobs: { job_id: string; dag: string; state: string; created_at: string | null }[];
  window_days: number;
  review?: {
    pending: number;
    decisions_this_month: Record<string, number>;
    median_review_seconds: number | null;
  };
  tool_health?: {
    tool: string;
    display_name: LocalizedText;
    runs: number;
    success_rate: number | null;
    controls_failed_rate: number | null;
    median_duration_seconds: number | null;
  }[];
};

/** A harvesting tool named for people. */
export type FigureSource = { key: string; name: string; display_name: LocalizedText };

export type MonthlyPoint = {
  period: number;
  amount: string | null;
  currency: string | null;
  job_id: string | null;
  run_at: string | null;
  state: string | null;
  /** the tool whose figures the point carries (the mapping, or the inputs until it runs) */
  source?: FigureSource | null;
};

/** One upload of a closing month: the run, and the file it was launched from. */
export type MonthRun = {
  job_id: string;
  state: string;
  /** when the run was launched */
  run_at: string | null;
  created_by: string;
  /** the uploaded entry file; null for a file-less (declared) run */
  file_name: string | null;
  uploaded_at: string | null;
  /** /stats/monthly/changes only: the run harvested figures for the month */
  harvested?: boolean;
  /** the tools the run executed, in order */
  tools?: string[];
  /** the last tool, named for people */
  tool_display_name?: LocalizedText;
  /** a tool that produces figures ran (its output becomes the chart) */
  harvests?: boolean;
  /** the workbook the run produced: what a next tool starts from */
  output_artifact_id?: string | null;
  /** when the run produces no figures by itself: the tool to run on its output */
  next_tool?: FigureSource | null;
  /** /stats/monthly/changes only: the tool whose figures this run contributes */
  figures_from?: FigureSource | null;
};

/** One closing month, upload by upload: every run launched for it (oldest
 *  first) and, per account and activity, the figure each successful upload
 *  harvested — the accounts that changed during the month. */
export type MonthlyChanges = {
  year: number;
  period: number;
  runs: MonthRun[];
  /** the job ids whose figures `accounts[].values` compare, oldest first */
  compared: string[];
  entities: string[];
  currency: string | null;
  accounts: MonthAccountChange[];
  changed_count: number;
  /** the tool whose figures the month shows */
  figures_from?: FigureSource | null;
  /** the tool that refines them, when the month has not reached it yet */
  next_tool?: FigureSource | null;
  /** the produced file that tool starts from */
  next_tool_input_artifact_id?: string | null;
};

export type MonthAccountChange = {
  fs_item: string;
  label: string;
  entity: string;
  /** one figure per compared upload; null = absent from that upload */
  values: (string | null)[];
  changed: boolean;
  first: string | null;
  last: string | null;
  /** last − first; null with fewer than two figures */
  delta: string | null;
};

/** Per closing month, each activity and their total, from the LAST successful
 *  run of each month: one Summary account, or the cumulative of every account
 *  when none is chosen — plus the closing calendar. */
export type MonthlyStats = {
  year: number;
  years: number[];
  /** the account charted; null = the cumulative of every account */
  fs_item: string | null;
  account_label: string;
  scope: "account" | "all_accounts";
  accounts: { fs_item: string; label: string }[];
  entities: string[];
  /** the key of the series that sums the activities ("TOTAL") */
  total_series: string;
  /** series[activity | total] = twelve points, January first */
  series: Record<string, MonthlyPoint[]>;
  calendar: {
    period: number;
    state: string | null;
    job_id: string | null;
    run_at: string | null;
    /** every upload launched for the month, newest first */
    runs: MonthRun[];
  }[];
};

export type SearchResults = {
  tools: { key: string; name: string; display_name: LocalizedText; description: LocalizedText }[];
  jobs: { job_id: string; dag: string; state: string; created_at: string | null }[];
  files: {
    artifact_id: string;
    display_name: string;
    kind: string;
    row_count: number;
    created_at: string | null;
  }[];
};

// ---- Authentication (AUTH_MODE=local) ------------------------------------

export type Role = "operator" | "approver" | "sysadmin";

export type AuthUser = {
  email: string;
  full_name: string;
  role: Role;
  must_change_password: boolean;
};

export type LoginResponse = { access_token: string; user: AuthUser };

export type MeResponse = AuthUser & {
  roles: string[];
  last_login_at?: string | null;
  customers?: CustomerSummary[];
  all_customers?: boolean;
};

/** A customer as the selector shows it. */
export type CustomerSummary = {
  id: string;
  name: string;
  code: string;
  status: "active" | "inactive";
};

/** Who edits a customer right now (persistence/leases.py). */
export type LeaseState = {
  customer_id: string;
  /** this tab holds the lease */
  mine: boolean;
  holder_subject: string | null;
  holder_name: string | null;
  since: string | null;
  ttl_s: number;
};

/** What an administrator types for a customer. */
export type CustomerInput = {
  name: string;
  code: string;
  sap_company_code: string;
  tagetik_reporting_unit: string;
  currency: string;
  phone: string;
  email: string;
  address: string;
  notes: string;
};

export type AdminCustomer = CustomerInput & {
  id: string;
  status: "active" | "inactive";
  created_at: string | null;
  updated_at: string | null;
};

export type AdminUser = {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  status: "pending" | "active" | "inactive" | "deleted";
  must_change_password: boolean;
  locked: boolean;
  approved_by: string | null;
  last_login_at: string | null;
  created_at: string | null;
  /** may work on every customer (sysadmins always do) */
  all_customers: boolean;
  /** the assigned customers when not every one */
  customer_ids: string[];
};

/** The API mount without its `/api` suffix — where root endpoints (/health) live. */
const ROOT = BASE.replace(/\/api\/?$/, "");

export const api = {
  tools: (signal?: AbortSignal) => request<PhaseGroup[]>("/tools", { signal }),

  stats: (signal?: AbortSignal) => request<UsageStats>("/stats", { signal }),

  monthly: (year?: number, fsItem?: string | null, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    if (year) params.set("year", String(year));
    if (fsItem) params.set("fs_item", fsItem);
    const query = params.toString();
    return request<MonthlyStats>(`/stats/monthly${query ? `?${query}` : ""}`, { signal });
  },

  monthlyChanges: (year: number, period: number, signal?: AbortSignal) =>
    request<MonthlyChanges>(`/stats/monthly/changes?year=${year}&period=${period}`, {
      signal,
    }),

  meta: (signal?: AbortSignal) => request<MetaResponse>("/meta", { signal }),

  search: (q: string, signal?: AbortSignal) =>
    request<SearchResults>(`/search?q=${encodeURIComponent(q)}`, { signal }),

  upload: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<UploadResult>("/files/upload", { method: "POST", body: form });
  },

  preflightTool: (payload: { tool: string; artifact_id: string }) =>
    request<{ tool: string; display_name: string; ok: boolean; errors: string[] }>(
      "/tools/preflight",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    ),

  runTool: (payload: {
    tool: string;
    /** null = a declared file-less run (see InputSlot.required_when). */
    artifact_id: string | null;
    overrides?: Record<string, string>;
    override_reason?: string;
    params?: Record<string, string>;
  }) =>
    request<{ job_id: string; step_id: string; state: string; display_name: string }>(
      "/tools/run",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    ),

  /** Served at the API root, not under /api — hence requestUrl + ROOT. */
  health: (signal?: AbortSignal) =>
    requestUrl<{ status: string; build: string }>(`${ROOT}/health`, { signal }),

  job: (jobId: string, signal?: AbortSignal) =>
    request<JobState>(`/jobs/${jobId}`, { signal }),
  /** The run list; `window` narrows it to the runs started in [from, to). */
  jobs: (limit?: number, signal?: AbortSignal, window?: { from?: string; to?: string }) => {
    const params = new URLSearchParams();
    if (limit) params.set("limit", String(limit));
    if (window?.from) params.set("created_from", window.from);
    if (window?.to) params.set("created_to", window.to);
    const query = params.toString();
    return request<JobSummary[]>(`/jobs${query ? `?${query}` : ""}`, { signal });
  },

  dags: (signal?: AbortSignal) => request<DagInfo[]>("/pipeline/dags", { signal }),

  startPipeline: (dag: string, artifactId?: string, params?: Record<string, string>) =>
    request<{ job_id: string; dag: string; state: string; steps: string[] }>(
      "/pipeline/start",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dag,
          artifact_id: artifactId ?? null,
          params: params ?? {},
        }),
      },
    ),

  pipeline: (jobId: string, signal?: AbortSignal) =>
    request<PipelineState>(`/pipeline/${jobId}`, { signal }),

  pending: (signal?: AbortSignal) =>
    request<PendingApproval[]>("/approvals/pending", { signal }),

  approve: (jobId: string, stepId: string, note: string) =>
    request<{ decision: string; approved_by: string }>(
      `/approvals/${jobId}/${stepId}/approve`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      },
    ),

  reject: (jobId: string, stepId: string, note: string) =>
    request<{ decision: string }>(`/approvals/${jobId}/${stepId}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note }),
    }),

  replace: (jobId: string, stepId: string, file: File, note: string) => {
    const form = new FormData();
    form.append("file", file);
    form.append("note", note);
    return request<{ decision: string; replacement_artifact_id: string }>(
      `/approvals/${jobId}/${stepId}/replace`,
      { method: "POST", body: form },
    );
  },

  /** Relaunch a FAILED / controls-blocked run as a NEW job. 409 with
   *  `body.tool` when the original never recorded its input (pre-upgrade). */
  retryJob: (jobId: string) =>
    request<{
      job_id: string;
      state: string;
      tool?: string;
      display_name?: string;
      retried_from: string;
    }>(`/jobs/${encodeURIComponent(jobId)}/retry`, { method: "POST" }),

  downloadUrl: (artifactId: string) =>
    request<{ url: string; display_name: string; expires_in_s: number }>(
      `/files/${artifactId}/download`,
    ),

  /**
   * Download an artifact as .xlsx (P2-05: intermediates are Parquet
   * internally, this always yields a workbook).
   *
   * FETCHED, never navigated: a plain `<a href>` is a browser navigation
   * and carries no Authorization header, so under AUTH_MODE=local/oidc it
   * died with 401 UNAUTHENTICATED. This goes through the authenticated
   * client, then hands the bytes to the browser as a normal save.
   */
  downloadExcel: async (artifactId: string, filename?: string): Promise<void> => {
    const response = await fetch(`${BASE}/files/${artifactId}/excel`, withAuth());
    if (!response.ok) {
      let body: ApiFailure = { status: `HTTP_${response.status}` };
      try {
        body = (await response.json()) as ApiFailure;
      } catch {
        /* non-JSON error body */
      }
      const summary =
        body.errors?.[0] ??
        body.detail ??
        i18n.t("api.downloadFailed", { status: response.status });
      throw new ApiError(summary, response.status, body);
    }
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
    const name =
      filename ??
      (match ? decodeURIComponent(match[1].replace(/"/g, "")) : `${artifactId}.xlsx`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },

  lineage: (artifactId: string, signal?: AbortSignal) =>
    request<Lineage>(`/files/${artifactId}/lineage`, { signal }),

  jobControls: (jobId: string, signal?: AbortSignal) =>
    request<RunControlsReport>(`/jobs/${jobId}/controls`, { signal }),

  /**
   * The same controls, as a printable report in plain language.
   *
   * FETCHED like downloadExcel, never navigated: a plain `<a href>` carries
   * no Authorization header and dies with 401 under AUTH_MODE=local/oidc.
   */
  downloadControlsPdf: async (jobId: string): Promise<void> => {
    const response = await fetch(`${BASE}/jobs/${jobId}/controls.pdf`, withAuth());
    if (!response.ok) {
      let body: ApiFailure = { status: `HTTP_${response.status}` };
      try {
        body = (await response.json()) as ApiFailure;
      } catch {
        /* non-JSON error body */
      }
      const summary =
        body.errors?.[0] ??
        body.detail ??
        i18n.t("api.downloadFailed", { status: response.status });
      throw new ApiError(summary, response.status, body);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `controles_${jobId.slice(0, 8)}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },

  /** Plan mode: the agent translates the instruction into edit operations —
   *  nothing is applied. `tool` lets the planner read the tool's declared
   *  file semantics (which sheets/cells the calculator actually consumes). */
  revisePlan: (
    artifactId: string,
    instruction: string,
    contextArtifactId?: string,
    tool?: string,
  ) =>
    request<RevisePlan>(`/files/${artifactId}/revise`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        instruction,
        context_artifact_id: contextArtifactId ?? null,
        tool: tool ?? null,
      }),
    }),

  /** Apply mode: the reviewed operations produce a NEW corrected artifact. */
  reviseApply: (artifactId: string, operations: EditOperation[]) =>
    request<ReviseApplied>(`/files/${artifactId}/revise`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operations }),
    }),

  ask: (question: string, jobId?: string, artifactId?: string) =>
    request<AuditAnswer & { attached?: boolean }>("/audit/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        job_id: jobId ?? null,
        artifact_id: artifactId ?? null,
      }),
    }),

  // ---- auth ----
  login: (email: string, password: string) =>
    request<LoginResponse>("/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    }),

  register: (email: string, fullName: string, password: string) =>
    request<{ status: string; detail: string }>("/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, full_name: fullName, password }),
    }),

  /** Silent refresh at boot: the HttpOnly cookie authenticates it.
   *  Deliberately exempt from the 401 replay — it IS the renewal. */
  refreshSession: (signal?: AbortSignal) =>
    requestNoRetry<LoginResponse>("/auth/refresh", { method: "POST", signal }),

  logout: () => request<{ status: string }>("/auth/logout", { method: "POST" }),

  me: (signal?: AbortSignal) => request<MeResponse>("/auth/me", { signal }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ status: string; detail: string }>("/auth/me/password", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    }),

  // ---- the edit lease on a customer ----
  leaseState: (customerId: string, signal?: AbortSignal) =>
    request<LeaseState>(`/customers/${customerId}/lease`, { signal }),
  /** Take the lease when free, keep it alive when mine, learn who holds it otherwise. */
  acquireLease: (customerId: string) =>
    request<LeaseState>(`/customers/${customerId}/lease`, { method: "POST" }),
  /** Drop my lease; `keepalive` lets the call outlive a closing tab. */
  releaseLease: (customerId: string, keepalive = false) =>
    request<{ status: string; released: boolean }>(`/customers/${customerId}/lease`, {
      method: "DELETE",
      keepalive,
    }),

  // ---- admin (sysadmin only) ----
  adminUsers: (signal?: AbortSignal) =>
    request<{ users: AdminUser[] }>("/admin/users", { signal }),

  /** Create an active account; the temporary password comes back exactly once. */
  adminCreateUser: (body: {
    email: string;
    full_name: string;
    role: Role;
    all_customers: boolean;
    customer_ids: string[];
  }) =>
    request<{ status: string; temporary_password: string; user: AdminUser }>("/admin/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  adminApprove: (userId: string, role: Role) =>
    request<{ status: string; user: AdminUser }>(`/admin/users/${userId}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    }),

  adminReject: (userId: string, note: string) =>
    request<{ status: string; user: AdminUser }>(`/admin/users/${userId}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note }),
    }),

  adminResetPassword: (userId: string) =>
    request<{ status: string; temporary_password: string; user: AdminUser }>(
      `/admin/users/${userId}/reset-password`,
      { method: "POST" },
    ),

  adminActivate: (userId: string) =>
    request<{ status: string; user: AdminUser }>(`/admin/users/${userId}/activate`, {
      method: "POST",
    }),

  adminDeactivate: (userId: string) =>
    request<{ status: string; user: AdminUser }>(`/admin/users/${userId}/deactivate`, {
      method: "POST",
    }),

  adminDelete: (userId: string) =>
    request<{ status: string; user: AdminUser }>(`/admin/users/${userId}`, {
      method: "DELETE",
    }),

  // ---- customers ------------------------------------------------------------
  customers: (signal?: AbortSignal) => request<CustomerSummary[]>("/customers", { signal }),

  adminCustomers: (signal?: AbortSignal) =>
    request<{ customers: AdminCustomer[] }>("/admin/customers", { signal }),

  adminCreateCustomer: (body: CustomerInput) =>
    request<{ status: string; customer: AdminCustomer }>("/admin/customers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  adminUpdateCustomer: (customerId: string, body: CustomerInput) =>
    request<{ status: string; customer: AdminCustomer }>(`/admin/customers/${customerId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  adminActivateCustomer: (customerId: string) =>
    request<{ status: string; customer: AdminCustomer }>(
      `/admin/customers/${customerId}/activate`,
      { method: "POST" },
    ),

  adminDeactivateCustomer: (customerId: string) =>
    request<{ status: string; customer: AdminCustomer }>(
      `/admin/customers/${customerId}/deactivate`,
      { method: "POST" },
    ),

  adminSetUserCustomers: (userId: string, body: { all_customers: boolean; customer_ids: string[] }) =>
    request<{ status: string; user: AdminUser }>(`/admin/users/${userId}/customers`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
};
